// Lightweight attention-grabber for Live Agent Help.
// Provides title flashing + favicon red-dot badge while the tab is inactive,
// and automatically clears itself when the tab regains focus.
//
// Usage:
//   import { notifyAgentEvent, clearAgentNotifications } from "../utils/agentNotify";
//   notifyAgentEvent("New message from agent");
//   clearAgentNotifications();   // call on session end / unmount

const FAVICON_SELECTOR = 'link[rel="icon"], link[rel="shortcut icon"]';

// Support-agent favicon — uses the headphones SVG asset shipped in /public/images.
const ALERT_FAVICON = "/images/headphones.svg";

// Notification sound shipped in /public/sounds. One play per new message.
const NOTIFY_SOUND_URL = "/sounds/LiveAgentNotificationSound.mp3";
const NOTIFY_SOUND_MUTE_KEY = "agentNotifyMuted";
// Hard floor between plays so a burst of messages can't overlap into a din.
const NOTIFY_SOUND_MIN_GAP_MS = 400;
let notifyAudio = null;
let lastSoundPlayedAt = 0;

const isSoundMuted = () => {
  try {
    return window.localStorage.getItem(NOTIFY_SOUND_MUTE_KEY) === "1";
  } catch (_) {
    return false;
  }
};

const getNotifyAudio = () => {
  if (notifyAudio) return notifyAudio;
  try {
    notifyAudio = new Audio(NOTIFY_SOUND_URL);
    notifyAudio.preload = "auto";
  } catch (_) {
    notifyAudio = null;
  }
  return notifyAudio;
};

// Browser autoplay policy blocks audio until the user has interacted with
// the page at least once. On the agent screen, the agent may receive the
// first user message before clicking anything, so we proactively "unlock"
// the <audio> element on the first user gesture by doing a muted play+pause.
// After this, real notification plays succeed immediately.
let audioUnlocked = false;
// If a notification fires before the audio is unlocked (autoplay blocked),
// remember it and play the sound the moment the agent first interacts.
let pendingPlayOnUnlock = false;
const unlockAudioOnce = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;
  const audio = getNotifyAudio();
  if (!audio) return;
  try {
    const wasMuted = audio.muted;
    audio.muted = true;
    const p = audio.play();
    const restore = () => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = wasMuted;
      } catch (_) { /* ignore */ }
      // Fire any sound that arrived before the user interacted.
      if (pendingPlayOnUnlock) {
        pendingPlayOnUnlock = false;
        // Reset the throttle so the queued play isn't suppressed.
        lastSoundPlayedAt = 0;
        playNotifySound();
      }
    };
    if (p && typeof p.then === "function") {
      p.then(restore).catch(() => { audio.muted = wasMuted; });
    } else {
      restore();
    }
  } catch (_) { /* ignore */ }
};

const installAudioUnlocker = () => {
  if (typeof window === "undefined") return;
  const events = ["pointerdown", "click", "keydown", "touchstart"];
  const handler = () => {
    unlockAudioOnce();
    events.forEach((ev) => window.removeEventListener(ev, handler, true));
  };
  events.forEach((ev) => window.addEventListener(ev, handler, true));
};
// Install on module load so the unlock is in place before the first message.
installAudioUnlocker();

const playNotifySound = () => {
  if (typeof window === "undefined") return;
  if (isSoundMuted()) return;
  const now = Date.now();
  if (now - lastSoundPlayedAt < NOTIFY_SOUND_MIN_GAP_MS) return;
  const audio = getNotifyAudio();
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
    const p = audio.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        lastSoundPlayedAt = now;
      }).catch(() => {
        // Autoplay blocked — queue a single play for the first user gesture.
        pendingPlayOnUnlock = true;
      });
    } else {
      lastSoundPlayedAt = now;
    }
  } catch (_) {
    pendingPlayOnUnlock = true;
  }
};

/**
 * Toggle / set the mute preference. Persisted in localStorage so it
 * survives reloads. Returns the new muted state.
 */
export const setAgentNotifyMuted = (muted) => {
  try {
    window.localStorage.setItem(NOTIFY_SOUND_MUTE_KEY, muted ? "1" : "0");
  } catch (_) { /* ignore */ }
  return !!muted;
};

export const isAgentNotifyMuted = () => isSoundMuted();

let originalTitle = null;
let originalFavicons = null;
let flashWorker = null;
let flashWorkerUrl = null;
let flashFallbackId = null;
let showAlertFrame = true;
// Per-source unread counts. Keys are caller-supplied category strings,
// e.g. "user", "agent", "system". Values are the unread message counts.
const pendingBySource = Object.create(null);
let focusListenerAttached = false;

const getFaviconLinks = () => Array.from(document.querySelectorAll(FAVICON_SELECTOR));

const captureOriginals = () => {
  if (originalTitle === null) originalTitle = document.title;
  if (originalFavicons === null) {
    originalFavicons = getFaviconLinks().map((link) => ({ link, href: link.href }));
  }
};

const setRedDotFavicon = () => {
  const links = getFaviconLinks();
  if (links.length === 0) {
    // No favicon link in the document — create one so the badge still shows.
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = ALERT_FAVICON;
    document.head.appendChild(link);
    return;
  }
  links.forEach((link) => {
    link.href = ALERT_FAVICON;
  });
};

const restoreFavicon = () => {
  if (!originalFavicons) return;
  originalFavicons.forEach(({ link, href }) => {
    if (link && link.parentNode) link.href = href;
  });
};

const buildAlertTitle = () => {
  const parts = Object.keys(pendingBySource)
    .filter((src) => pendingBySource[src] > 0)
    .map((src) => {
      const n = pendingBySource[src];
      const noun = n === 1 ? "New Message" : "New Messages";
      const who = src
        ? src.charAt(0).toUpperCase() + src.slice(1)
        : "";
      return who ? `${n} ${noun} from ${who}` : `${n} ${noun}`;
    });
  return parts.length ? parts.join(", ") : "New activity";
};

const renderTitleFrame = () => {
  if (showAlertFrame) {
    document.title = buildAlertTitle();
  } else {
    document.title = originalTitle || document.title;
  }
  showAlertFrame = !showAlertFrame;
};

// Background browser tabs throttle setInterval to ~1 fire/min, which makes
// the title appear stuck. A Web Worker timer is NOT throttled, so we drive
// the flash from a worker and only mutate document.title on the main thread.
const WORKER_SOURCE = `
let id = null;
self.onmessage = (e) => {
  if (e.data && e.data.type === 'start') {
    if (id) clearInterval(id);
    id = setInterval(() => self.postMessage('tick'), e.data.interval || 1000);
  } else if (e.data && e.data.type === 'stop') {
    if (id) { clearInterval(id); id = null; }
  }
};
`;

const startFlashing = () => {
  if (flashWorker || flashFallbackId) return;
  showAlertFrame = true;
  // Render first frame immediately (no waiting on the timer at all).
  renderTitleFrame();

  try {
    const blob = new Blob([WORKER_SOURCE], { type: "application/javascript" });
    flashWorkerUrl = URL.createObjectURL(blob);
    flashWorker = new Worker(flashWorkerUrl);
    flashWorker.onmessage = renderTitleFrame;
    flashWorker.postMessage({ type: "start", interval: 1000 });
  } catch (err) {
    // Workers blocked (e.g. strict CSP) — fall back to setInterval. Will be
    // throttled when hidden but still works when the window is visible.
    flashFallbackId = setInterval(renderTitleFrame, 1000);
  }
};

const stopFlashing = () => {
  if (flashWorker) {
    try {
      flashWorker.postMessage({ type: "stop" });
      flashWorker.terminate();
    } catch (_) { /* ignore */ }
    flashWorker = null;
  }
  if (flashWorkerUrl) {
    URL.revokeObjectURL(flashWorkerUrl);
    flashWorkerUrl = null;
  }
  if (flashFallbackId) {
    clearInterval(flashFallbackId);
    flashFallbackId = null;
  }
};

const handleFocusReturn = () => {
  // Only clear when THIS tab is actually the visible one. A focus event on
  // the window can fire while the user is on a different tab in the same
  // browser, so visibilityState is the authoritative signal.
  if (document.visibilityState === "visible") {
    clearAgentNotifications();
  }
};

const attachFocusListener = () => {
  if (focusListenerAttached) return;
  document.addEventListener("visibilitychange", handleFocusReturn);
  window.addEventListener("focus", handleFocusReturn);
  focusListenerAttached = true;
};

const detachFocusListener = () => {
  if (!focusListenerAttached) return;
  document.removeEventListener("visibilitychange", handleFocusReturn);
  window.removeEventListener("focus", handleFocusReturn);
  focusListenerAttached = false;
};

// "Active" means the tab is the foreground tab AND its window has focus.
// This treats all of the following as inactive (so notifications fire):
//   - user is on a different tab in the same browser window
//   - user is on a different browser window / different app
//   - browser is minimized
const isTabActive = () =>
  document.visibilityState === "visible" && document.hasFocus();

/**
 * Trigger an attention notification (title flash + favicon dot).
 * No-op if the tab is currently active. Safe to call repeatedly:
 * subsequent calls bump per-source counters shown in the title
 * (e.g. "1 new message from user, 2 new messages from agent").
 *
 * @param {string} source - Short label for the message origin
 *                          ("user", "agent", "system", ...).
 */
export const notifyAgentEvent = (source) => {
  if (typeof document === "undefined") return;

  // Notify (sound + title flash + favicon) only when this tab is not active —
  // i.e. user is on a different tab, different window, or browser is minimized.
  if (isTabActive()) return;

  playNotifySound();

  captureOriginals();
  const key = (source || "").toString().trim().toLowerCase() || "agent";
  pendingBySource[key] = (pendingBySource[key] || 0) + 1;
  setRedDotFavicon();
  startFlashing();
  attachFocusListener();
};

/**
 * Stop flashing the title, restore favicon, and reset state.
 * Call on session end (Quit Agent, timeout, unmount).
 */
export const clearAgentNotifications = () => {
  if (typeof document === "undefined") return;
  stopFlashing();
  if (originalTitle !== null) document.title = originalTitle;
  restoreFavicon();
  detachFocusListener();
  Object.keys(pendingBySource).forEach((k) => delete pendingBySource[k]);
  originalTitle = null;
  originalFavicons = null;
};
